import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, Order, Product } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import './Account.css';
import { proxyBucketUrl } from '../utils/imageUtils';

const Account: React.FC = () => {
  const { user, isAuthenticated, updateUser, loading: userLoading } = useUser();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  // Check for tab parameter in URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['profile', 'orders', 'favorites'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    // Wait for user loading to complete before checking authentication
    if (userLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postalCode: user.postalCode || '',
        country: user.country || ''
      });
      loadOrders();
      loadFavorites();
    }
  }, [user, isAuthenticated, userLoading, navigate]);

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const userOrders = await api.getUserOrders();
      setOrders(userOrders);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      setFavoritesLoading(true);
      const favoritesData = await api.getFavorites();
      setFavorites(favoritesData);
      setFavoritesCount(favoritesData.length);
    } catch (error) {
      console.error('Failed to load favorites:', error);
      setFavorites([]);
      setFavoritesCount(0);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleRemoveFromFavorites = async (productId: number) => {
    try {
      await api.removeFromFavorites(productId);
      setFavorites(favorites.filter(product => product.id !== productId));
      setFavoritesCount(favoritesCount - 1);
    } catch (err: any) {
      console.error('Failed to remove from favorites:', err);
      showToast('Failed to remove from favorites. Please try again.', 'error');
    }
  };

  const toggleOrderDetails = (orderId: number) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Here we would normally make an API call to update the user
      // For now, we'll just update the local state
      updateUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country
      });

      setMessage('Your information has been updated successfully!');
      setIsEditing(false);
    } catch (error) {
      setMessage('Error updating your information.');
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postalCode: user.postalCode || '',
        country: user.country || ''
      });
    }
    setIsEditing(false);
  };

  if (userLoading || !user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="account-page">
      <div className="account-hero">
        <div className="account-container">
          <div className="account-header">
            <h1>My Account</h1>
            <p>Manage your profile & wishlist</p>
          </div>
        </div>
      </div>
      <div className="account-container">

        <div className="account-content">
          <div className="account-sidebar">
            <div className="user-info">
              <h3>{user.firstName} {user.lastName}</h3>
              <p>{user.email}</p>
            </div>

            <nav className="account-nav">
              <button 
                className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                <span className="nav-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Profile
              </button>
              <button 
                className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('orders')}
              >
                <span className="nav-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 8C21 8 21 8 21 8L12 13L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 8V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Orders
              </button>
              <button 
                className={`nav-item ${activeTab === 'favorites' ? 'active' : ''}`}
                onClick={() => setActiveTab('favorites')}
              >
                <span className="nav-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Wishlist
              </button>
            </nav>
          </div>

          <div className="account-main">
            {activeTab === 'profile' && (
              <>
                <div className="profile-section">
                  <div className="profile-section-header">
                    <h2>Personal Information</h2>
                    {!isEditing ? (
                      <button className="edit-btn" onClick={() => setIsEditing(true)}>
                        <span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="#197cc7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        Edit
                      </button>
                    ) : (
                      <div className="edit-actions">
                        <button className="save-btn" onClick={handleSave} disabled={loading}>
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="cancel-btn" onClick={handleCancel}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {message && (
                    <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
                      {message}
                    </div>
                  )}

                  <form className="profile-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        disabled
                        className="disabled-field"
                      />
                      <small>Email cannot be changed</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="address">Street Address</label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your street address"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="city">City</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="Enter your city"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="postalCode">Postal Code</label>
                        <input
                          type="text"
                          id="postalCode"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="Enter postal code"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="country">Country</label>
                      <input
                        type="text"
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your country"
                      />
                    </div>
                  </form>
                </div>

              </>
            )}

            {activeTab === 'orders' && (
              <div className="orders-section">
                <div className="profile-section-header">
                  <h2>My Orders</h2>
                </div>
                
                {ordersLoading ? (
                  <div className="loading">Loading orders...</div>
                ) : orders.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 8C21 8 21 8 21 8L12 13L3 8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3 8V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3>No orders yet</h3>
                    <p>You haven't placed any orders yet. Start shopping to see your orders here!</p>
                    <button className="shop-btn" onClick={() => navigate('/shop')}>
                      Start Shopping
                    </button>
                  </div>
                ) : (
                  <div className="orders-list">
                    {orders.map(order => (
                      <div key={order.id} className="order-card">
                        <div className="order-main" onClick={() => toggleOrderDetails(order.id)}>
                          <div className="order-image">
                            <img 
                              src={proxyBucketUrl(order.items?.[0]?.product?.images?.[0]) || '/products/placeholder.svg'} 
                              alt="Order"
                            />
                          </div>
                          <div className="order-info">
                            <h3>Order #{order.id}</h3>
                            <p>{order.items?.length || 0} items</p>
                            <div className="order-meta">
                              <span className={`status status-${order.status?.toLowerCase() || 'pending'}`}>
                                {order.status || 'Pending'}
                              </span>
                              <span className="order-date">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="order-total">€{Number(order.totalAmount || 0).toFixed(2)}</div>
                          <button className="expand-btn">
                            {expandedOrder === order.id ? '−' : '+'}
                          </button>
                        </div>

                        {expandedOrder === order.id && (
                          <div className="order-details-expanded">
                            <div className="details-section">
                              <h4>Order Information</h4>
                              <div className="details-grid">
                                <div className="detail-item">
                                  <span className="detail-label">Order Number:</span>
                                  <span className="detail-value">#{order.id}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Order Date:</span>
                                  <span className="detail-value">{new Date(order.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Status:</span>
                                  <span className={`status status-${order.status?.toLowerCase() || 'pending'}`}>
                                    {order.status || 'Pending'}
                                  </span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Payment Method:</span>
                                  <span className="detail-value">
                                    {order.paymentMethod === 'CASH' ? '💵 Cash on Delivery' : 
                                     order.paymentMethod === 'CARD' ? '💳 Credit Card' :
                                     order.paymentMethod === 'PAYPAL' ? '🌐 PayPal' :
                                     '💳 Credit Card'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {order.items && order.items.length > 0 && (
                              <div className="details-section">
                                <h4>Items ({order.items.length})</h4>
                                <div className="order-items-list">
                                  {order.items.map((item, index) => (
                                    <div key={index} className="order-item">
                                      <img 
                                        src={proxyBucketUrl(item.product?.images?.[0]) || '/products/placeholder.svg'} 
                                        alt={item.product?.name || 'Product'}
                                      />
                                      <div className="item-details">
                                        <h5>{item.product?.name || 'Unknown Product'}</h5>
                                        {item.size && <p>Size: {item.size}</p>}
                                        <p className="item-price">€{Number(item.product?.price || 0).toFixed(2)}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="details-section">
                              <h4>Delivery Information</h4>
                              <div className="details-grid">
                                <div className="detail-item">
                                  <span className="detail-label">Delivery Address:</span>
                                  <span className="detail-value">
                                    {order.shippingAddress || 'Not specified'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'favorites' && (
              <div className="favorites-section">
                <div className="profile-section-header">
                  <h2>My Wishlist</h2>
                </div>
                
                <div className="wishlist-header">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Wishlist</h3>
                  <p>{favorites.length === 0 ? 'Items you add to your wishlist will appear here for easy access!' : `Your favorite vintage treasures collected in one place! (${favorites.length} ${favorites.length === 1 ? 'item' : 'items'})`}</p>
                  {favorites.length === 0 && (
                    <>
                      <div style={{marginTop: '2rem'}}></div>
                      <button className="shop-btn" onClick={() => navigate('/shop')}>
                        Discover Vintage Treasures
                      </button>
                    </>
                  )}
                </div>
                
                {favoritesLoading ? (
                  <div className="loading">Loading favorites...</div>
                ) : favorites.length > 0 && (
                  <div className="favorites-grid">
                    {favorites.map(product => (
                      <div key={product.id} className="favorite-item" onClick={() => navigate(`/product/${product.id}`)}>
                        <div className="product-card-inner">
                          <div className="product-image-wrapper">
                            <button 
                              className="remove-favorite-btn active"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFromFavorites(product.id);
                              }}
                              title="Remove from favorites"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#ffffff" strokeWidth="1.5"/>
                              </svg>
                            </button>
                            
                            <div className="product-images">
                              <img 
                                src={proxyBucketUrl(product.images?.[0]) || '/products/placeholder.svg'} 
                                alt={product.name} 
                                className="primary-image"
                              />
                            </div>
                          </div>
                          
                          <div className="product-info">
                            <Link to={`/product/${product.id}`} className="product-name-link">
                              <h3 className="product-name">{product.name}</h3>
                            </Link>
                            
                            <div className="product-footer">
                              <div className="price-section">
                                <span className="current-price">€{Number(product.price).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;